import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { categories, getDramasByCategory, Drama } from '@/mocks/dramas';

function Breadcrumb({ items }: { items: { label: string; href?: string }[] }) {
  return (
    <nav className="flex items-center gap-1.5 text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
      {items.map((item, i) => (
        <span key={i} className="flex items-center gap-1.5">
          {i > 0 && <i className="ri-arrow-right-s-line text-xs"></i>}
          {item.href ? (
            <Link to={item.href} className="hover:text-primary-600 transition-colors whitespace-nowrap">
              {item.label}
            </Link>
          ) : (
            <span className="text-foreground-800 font-medium whitespace-nowrap">{item.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');
  const [dramas, setDramas] = useState<Drama[]>([]);

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  useEffect(() => {
    setDramas(getDramasByCategory(activeCategory));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeCategory]);

  const handleCategoryChange = (catSlug: string) => {
    setActiveCategory(catSlug);
    navigate(`/category/${catSlug}`, { replace: true });
  };

  const activeCatName = categories.find((c) => c.slug === activeCategory)?.name || '全部';

  return (
    <main className="pt-20 min-h-screen bg-background-50">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-8">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '分类浏览' },
            { label: `${activeCatName}剧` },
          ]}
        />

        <div className="mb-8">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
            {activeCatName === '全部' ? '全部电视剧' : `${activeCatName}剧`}
          </h1>
          <p className="text-sm text-foreground-500 mt-2">
            共 {dramas.length} 部{activeCatName === '全部' ? '' : activeCatName}电视剧
          </p>
        </div>

        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((cat) => (
            <button
              key={cat.slug}
              onClick={() => handleCategoryChange(cat.slug)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === cat.slug
                  ? 'bg-primary-500 text-background-50 shadow-sm'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              <i className={`${cat.icon} text-sm`}></i>
              {cat.name}
            </button>
          ))}
        </div>

        {dramas.length === 0 ? (
          <div className="text-center py-20">
            <span className="w-16 h-16 flex items-center justify-center bg-background-100 rounded-full mx-auto mb-4">
              <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
            </span>
            <p className="text-foreground-500">暂无该分类的电视剧</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 md:gap-5 stagger-children">
            {dramas.map((drama) => (
              <Link
                key={drama.id}
                to={`/drama/${drama.id}`}
                className="group cursor-pointer"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3">
                  <img
                    src={drama.coverImage}
                    alt={drama.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                    <span className="text-white text-xs font-medium bg-primary-500/90 px-2 py-1 rounded whitespace-nowrap">
                      {drama.rating} 分
                    </span>
                  </div>
                  {drama.status === '更新中' && (
                    <span className="absolute top-2 left-2 bg-accent-500 text-background-50 text-xs px-2 py-0.5 rounded font-medium whitespace-nowrap">
                      更新中
                    </span>
                  )}
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                  {drama.title}
                </h3>
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-xs text-secondary-600 bg-secondary-100 px-1.5 py-0.5 rounded whitespace-nowrap">
                    {categories.find((c) => c.slug === drama.categories[0])?.name}
                  </span>
                  <span className="text-xs text-foreground-400">{drama.episodes}集</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}